import {
    useAppDisplayModeContext,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import type { DirectoryItemThumbFragment } from 'generated/graphql';
import ProductTile from '~/modules/tile/component/product-tile/ProductImpressionTile';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import ProjectTile from '~/modules/tile/component/project-tile/ProjectImpressionTile';
import getProductPriceDisplayProps from '~/modules/tile/util/get-product-tile-price-display-props';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import type { SaveToDesignBoardProps } from '~/modules/design-board/component/SaveToDesignBoard';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';
import { getAdditionalVariantCount } from '@modules/page-builder/util/get-additional-variant-count';
import {
    RecentlyViewedProjectTile,
    RecentlyViewedProfessionalTile,
} from './RecentlyViewed.style';

interface RecentlyViewedItemsProps {
    recentlyViewedItem: DirectoryItemThumbFragment;
}

const TILE_FALLBACK_IMAGE_SRC = '';

const getTileImageString = (data: DirectoryItemThumbFragment) => {
    switch (data.__typename) {
        case 'Professional':
            const detailBanner =
                data.RecentlyViewedImage?.LibraryImageFile
                    ?.DetailBannerStandard;
            return (
                detailBanner?.Desktop ??
                data.CompanyLogo ??
                TILE_FALLBACK_IMAGE_SRC
            );
        case 'Product':
            return (
                data.RecentlyViewedImage?.LibraryImageFile
                    ?.ThumbnailDirectoryTile ?? TILE_FALLBACK_IMAGE_SRC
            );
        case 'Project':
            return (
                data.RecentlyViewedImage?.LibraryImageFile
                    ?.PhotoTileDraggable ?? TILE_FALLBACK_IMAGE_SRC
            );
        default:
            return TILE_FALLBACK_IMAGE_SRC;
    }
};

export const RecentlyViewedTiles = (props: RecentlyViewedItemsProps) => {
    const { recentlyViewedItem } = props;
    const { mobile, desktop } = useAppDisplayModeContext();
    const { css } = useStyles();
    const tileImageSrcString = getTileImageString(recentlyViewedItem);
    const isValidPin = [
        'ProductPin',
        'ProjectPin',
        'ProfessionalPin',
        'ArticlePin',
    ].find(
        (pin) => pin === `${recentlyViewedItem.__typename}Pin`
    ) as SaveToDesignBoardProps['itemType'];

    const { favouriteItem } = useSaveToDesignBoard(
        isValidPin,
        recentlyViewedItem.ID
    );

    const { price, pricePrefix } = getProductPriceDisplayProps(
        recentlyViewedItem.__typename === 'Product'
            ? recentlyViewedItem.PricingDisplay
            : null
    );
    const variantColors =
        recentlyViewedItem.__typename === 'Product'
            ? recentlyViewedItem?.Colors ?? []
            : [];

    const additionalVariantCount =
        getAdditionalVariantCount(variantColors?.length ?? 0) ?? 0;

    return (
        <>
            {favouriteItem.target && (
                <SaveToDesignBoard {...favouriteItem} autoReposition={false} />
            )}
            {recentlyViewedItem.__typename === 'Product' && (
                <ProductTile
                    key={recentlyViewedItem.Link}
                    as={Link}
                    to={recentlyViewedItem.Link}
                    prefetch="intent"
                    size={desktop ? 'large' : 'xsmall'}
                    variant={desktop ? '01' : '02'}
                    newDesignEnabled={true}
                    image={[
                        {
                            src: tileImageSrcString,
                            alt: recentlyViewedItem?.Title,
                            mixBlendMode: recentlyViewedItem.LibraryThumb
                                ?.LibraryImage?.IsMultiplied
                                ? 'multiply'
                                : 'normal',
                            sizes: getImagesSizes(348),
                        },
                    ]}
                    price={price}
                    pricePrefix={pricePrefix}
                    productName={{
                        children: recentlyViewedItem?.Title,
                    }}
                    professionalName={`By ${recentlyViewedItem.Professional?.Title}`}
                    onFavourite={(e) =>
                        favouriteItem.onClick(e, recentlyViewedItem.ID)
                    }
                    isFavourited={favouriteItem.favourited(
                        recentlyViewedItem.ID
                    )}
                    variants={variantColors.map(({ ID, Label, Dot, Code }) => ({
                        key: `variant-dot-${ID}`,
                        children: (
                            <VariantDot
                                size={mobile ? 9 : 13}
                                colorCode={Code ?? ''}
                                description={Label}
                                image={Dot?.[mobile ? 'Mobile' : 'Desktop']}
                            />
                        ),
                    }))}
                    additionalVariantCount={additionalVariantCount}
                    impression={{
                        professionalID:
                            recentlyViewedItem.Professional?.ID ?? 0,
                        itemID: recentlyViewedItem.ID,
                        type: 'RecentlyViewed',
                    }}
                />
            )}
            {recentlyViewedItem.__typename === 'Project' && (
                <ProjectTile
                    key={recentlyViewedItem.Link}
                    as={Link}
                    prefetch="intent"
                    to={recentlyViewedItem.Link}
                    size={desktop ? 'large' : 'small'}
                    variant={desktop ? '01' : '02'}
                    newDesignEnabled={true}
                    className={css(RecentlyViewedProjectTile)}
                    image={[
                        {
                            src: tileImageSrcString,
                            alt: recentlyViewedItem?.Title,
                            sizes: getImagesSizes(588),
                        },
                    ]}
                    projectName={recentlyViewedItem.Title}
                    professionalName={recentlyViewedItem.Professional?.Title}
                    onFavourite={(e) =>
                        favouriteItem.onClick(e, recentlyViewedItem.ID)
                    }
                    isFavourited={favouriteItem.favourited(
                        recentlyViewedItem.ID
                    )}
                    impression={{
                        professionalID:
                            recentlyViewedItem.Professional?.ID ?? 0,
                        itemID: recentlyViewedItem.ID,
                        type: 'RecentlyViewed',
                    }}
                />
            )}
            {recentlyViewedItem.__typename === 'Professional' && (
                <ProfessionalTile
                    key={recentlyViewedItem.Link}
                    as={Link}
                    prefetch="intent"
                    to={recentlyViewedItem.Link}
                    size={desktop ? 'large' : 'xsmall'}
                    variant={'04'}
                    className={css(RecentlyViewedProfessionalTile)}
                    image={[
                        {
                            src: tileImageSrcString,
                            alt: recentlyViewedItem?.Title,
                            sizes: getImagesSizes(593),
                            loading: 'lazy',
                        },
                    ]}
                    logo={{
                        image: {
                            src: recentlyViewedItem.CompanyLogo || '',
                            alt: `${recentlyViewedItem?.Title} professional logo`,
                        },
                        variables: {
                            avatarImageBackgroundColor:
                                recentlyViewedItem.LogoBackgroundColor?.toString() ??
                                'white',
                        },
                    }}
                    category={recentlyViewedItem.Categories?.[0]?.PrettyTitle}
                    title={recentlyViewedItem.Title}
                    services={recentlyViewedItem.Services?.slice(0, 3)}
                    memberSince={
                        recentlyViewedItem.ProfileLiveDate &&
                        new Date(
                            recentlyViewedItem.ProfileLiveDate
                        ).getFullYear()
                    }
                    onFavourite={(e) =>
                        favouriteItem.onClick(e, recentlyViewedItem.ID)
                    }
                    isFavourited={favouriteItem.favourited(
                        recentlyViewedItem.ID
                    )}
                    impression={{
                        professionalID: recentlyViewedItem.ID,
                        itemID: recentlyViewedItem.ID,
                        type: 'RecentlyViewed',
                    }}
                    variables={{
                        contentTilePadding: pxToRem(10),
                    }}
                    hideFavourite={true}
                />
            )}
        </>
    );
};
